import { useRef } from "react";

/** A readonly set or array of type T. */
export type ReadonlySetOrArray<T> = Readonly<Set<T>> | T[];

/**
 * Compute the difference of two sets.
 * @param minuend The set we start with.
 * @param subtrahend The set we take away.
 * @returns The difference (remainder of minuend).
 * If minuend is a StrictlyFrozenSet, the result will also be a StrictlyFrozenSet, and will be stable when applicable.
 */
export function setMinus<T>(minuend: StrictlyFrozenSet<T>, subtrahend: Readonly<Set<T>>): StrictlyFrozenSet<T>;
export function setMinus<T>(minuend: ReadonlySetOrArray<T>, subtrahend: Readonly<Set<T>>): Set<T>;
export function setMinus<T>(minuend: StrictlyFrozenSet<T> | ReadonlySetOrArray<T>, subtrahend: Readonly<Set<T>>) {
    // less concise, more performant
    const difference = new Set<T>();
    for (const item of minuend) {
        if (!subtrahend.has(item)) {
            difference.add(item);
        }
    }

    if (!isFrozen(minuend)) {
        return difference;
    }

    return difference.size !== minuend.size ? setFreeze(difference) : minuend;
}

/**
 * Compute the intersection of two sets.
 * If one set is likely to be a subset of the other, for best performance, pass the subset as first.
 * @param first The first set.
 * @param second The second set.
 * @returns The intersection of the sets (elements in common).
 * If first is a StrictlyFrozenSet, the result will also be a StrictlyFrozenSet, and will be stable when applicable.
 */
export function setIntersection<T>(first: StrictlyFrozenSet<T>, second: Readonly<Set<T>>): StrictlyFrozenSet<T>;
export function setIntersection<T>(first: ReadonlySetOrArray<T>, second: Readonly<Set<T>>): Set<T>;
export function setIntersection<T>(first: StrictlyFrozenSet<T> | ReadonlySetOrArray<T>, second: Readonly<Set<T>>) {
    if (isSubset<T>(first, second)) {
        return !isFrozen(first) ? new Set<T>(first) : first;
    }

    return setIntersectionCore<T>(first, second);
}

function setIntersectionCore<T>(first: ReadonlySetOrArray<T>, second: Readonly<Set<T>>) {
    // less concise, more performant
    const intersection = new Set<T>();
    for (const item of first) {
        if (second.has(item)) {
            intersection.add(item);
        }
    }

    return intersection;
}

function isSubset<T>(candidate: ReadonlySetOrArray<T>, of: Readonly<Set<T>>) {
    for (const item of candidate) {
        if (!of.has(item)) {
            return false;
        }
    }
    return true;
}

/**
 * Compute the union of two sets.
 * @param first The first set.
 * @param second The second set.
 * @returns The union of the two sets.
 * If first is a StrictlyFrozenSet, the result will also be a StrictlyFrozenSet, and will be stable when applicable.
 */
export function setUnion<T>(first: StrictlyFrozenSet<T>, second: ReadonlySetOrArray<T>): StrictlyFrozenSet<T>;
export function setUnion<T>(first: ReadonlySetOrArray<T>, second: ReadonlySetOrArray<T>): Set<T>;
export function setUnion<T>(first: StrictlyFrozenSet<T> | ReadonlySetOrArray<T>, second: ReadonlySetOrArray<T>) {
    const union = new Set<T>([...first, ...second]);
    if (!isFrozen(first)) {
        return union;
    }

    return union.size > first.size ? setFreeze(union) : first;
}

/**
 * Determine if two sets are equal, ignoring insertion order.
 * @param second The first set.
 * @param first The second set.
 * @returns Whether or not the sets are equal.
 */
export function setEquals<T>(first: ReadonlySetOrArray<T>, second: ReadonlySetOrArray<T>) {
    if (first === second) {
        return true;
    }

    if (getLength(second) !== getLength(first)) {
        return false;
    }

    // set sizes are equal, so we can use isSubset to determine equality
    const secondSet = Array.isArray(second) ? new Set<T>(second) : second;
    return isSubset<T>(first, secondSet);
}

function getLength<T>(x: ReadonlySetOrArray<T>) {
    if (Array.isArray(x)) {
        return x.length;
    } else {
        return x.size;
    }
}

const frozen: unique symbol = Symbol("frozen set");

/** Immutable map type. Branded to indicate that a particular instance is immutable forevermore. */
export interface StrictlyFrozenSet<T> extends Readonly<Set<T>> {
    [frozen]: true;
}

/**
 * Freeze a set, making it immutable.
 * @param set The original set.
 * @returns The same set reference, after freezing the set.
 */
export function setFreeze<T>(set: Set<T>) {
    if (isFrozen<T>(set)) {
        return set; // as StrictlyFrozenSet<T>;
    }

    setFreezeMethod(set, "add");
    setFreezeMethod(set, "delete");
    setFreezeMethod(set, "clear");
    Object.defineProperty(set, frozen, { value: true });

    return Object.freeze(set) as StrictlyFrozenSet<T>;
}

function isFrozen<T>(set: unknown): set is StrictlyFrozenSet<T> {
    return typeof set === "object" && !!set && frozen in set && !!set[frozen];
}

function setFreezeMethod<T>(set: Set<T>, key: keyof Set<T>) {
    Object.defineProperty(set, key, {
        value: () => {
            throw new Error("A frozen set cannot be modified.");
        },
    });
}

/**
 * Build a read-only set from an iterable.
 * @param iterable The original iterable.
 * @returns A read-only set containing values from the iterable.
 */
export function toReadonlySet<T>(iterable: Iterable<T>) {
    if (isFrozen<T>(iterable)) {
        return iterable;
    }

    return setFreeze(new Set<T>(iterable));
}

/** When a set reference changes, but the new set is set-equal to the previous set, returns
 * the same reference as the previous set. */
export function useStableSet<T>(newSet: Set<T>) {
    const ref = useStableSetRef<T>();
    // eslint-disable-next-line react-hooks/refs, react-compiler/react-compiler
    return setFreeze<T>((ref.current = setEquals<T>(newSet, ref.current) ? ref.current : setFreeze<T>(newSet)));
}

function useStableSetRef<T>() {
    return useRef(toReadonlySet<T>([]));
}

/** Enumerates entries in allRows that are not yet present in seenRows and adds them to seenRows.
 * Returns a new set bearing entries in allRows that were not yet present in seenRows.
 * SIDE EFFECT: seenRows may be added to in-place. */
export function updateSeenRows<T>(allRows: ReadonlySet<T>, seenRows: Set<T>) {
    const newRows = new Set<T>();
    for (const item of allRows) {
        if (!seenRows.has(item)) {
            newRows.add(item);
            seenRows.add(item);
        }
    }

    return newRows;
}
